'use client';

import { Header } from '@/payload-types';
import { useEffect, useRef, useState } from 'react';
import { WithTheme } from '@/types/WithTheme';
import { clsx } from 'clsx';
import OverflowMenu from '@/icons/OverflowMenu';
import { cn } from '@/utilities/cn';
import DHeaderItem from '@/globals/Header/components/desktop/DHeaderItem';
import DHeaderHiddenItem from '@/globals/Header/components/desktop/DHeaderHiddenItem';
import DHeaderSearch from '@/globals/Header/components/desktop/DHeaderSearch';

type IHeaderSecondRowProps = WithTheme & {
  secondRow: Header['secondRow'];
};

type NavItems = Exclude<Header['secondRow'], undefined>['navItems'];

export default function DHeaderSecondRow(props: IHeaderSecondRowProps) {
  const { secondRow, theme } = props;
  const { navItems = [] } = secondRow || {};
  const textColor = theme?.colors?.button?.primaryButtonTextColor || 'black';

  const headerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLFormElement>(null);

  const [visibleLinksId, setVisibleLinksId] = useState<string[]>(
    navItems?.map(item => item.id || '') || [],
  );
  const [hiddenLinks, setHiddenLinks] = useState<NavItems>([]);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const toggleOverflow = () => setOverflowOpen(!overflowOpen);

  const linksWidth = useRef<number[]>([]);
  const linksRef = useRef<HTMLLIElement[]>([]);

  const moreButtonWidth = 40;
  const gap = 16;

  useEffect(() => {
    // Get the initial width of elements
    linksWidth.current = linksRef.current.map(el => el.offsetWidth);

    const resizeHandler = () => {
      // Calculate the number of buttons that will fit by adding them one by one
      let combinedWidth = 0;
      const availableWidth =
        (headerRef?.current?.offsetWidth || 0) -
        (searchRef.current?.offsetWidth || 0) -
        moreButtonWidth;

      const newVisibleItemsId: string[] = [];

      linksWidth.current.forEach((width, idx) => {
        if (combinedWidth + (width + gap) < availableWidth) {
          combinedWidth += width + gap;
          newVisibleItemsId.push(navItems?.[idx]?.id || '');
        }
      });

      setVisibleLinksId(newVisibleItemsId);

      // The rest will be marked as hidden links
      const newHiddenLinks = JSON.parse(JSON.stringify(navItems || [])).splice(
        newVisibleItemsId.length,
        (navItems?.length || 1) - 1,
      );
      setHiddenLinks(newHiddenLinks);
    };

    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-secondary">
      <div className="container flex items-center" ref={headerRef}>
        {/* NAVIGATION */}
        <nav ref={navRef} className="flex-1">
          <ul
            className={clsx(
              'flex flex-nowrap',
              textColor === 'white' ? 'text-white' : 'text-black',
            )}
            style={{ gap: `${gap}px` }}
          >
            {
              navItems?.map((item, index) => (
                <li
                  key={`${item.id}_${index}`}
                  data-id={item.id}
                  ref={(el: HTMLLIElement) => {
                    linksRef.current[index] = el;
                  }}
                  className={clsx(
                    'group relative z-10',
                    !visibleLinksId.includes(item.id || '') && 'hidden',
                  )}
                >
                  <DHeaderItem item={item} />
                </li >
              ))
            }
          </ul >
        </nav >

        {/* OVERFLOW MENU */}
        {
          Boolean(hiddenLinks?.length) && (
            <div
              style={{ width: `${moreButtonWidth}px` }}
              className="relative flex items-center justify-center"
            >
              <button
                onClick={toggleOverflow}
                className="cursor-pointer transition-all duration-400 hover:-translate-y-0.5"
                aria-label='Toggle overflow menu'
              >
                <OverflowMenu width={32} height={32} color="#F2F8FE" />
              </button>
              <ul
                className={cn(
                  'absolute right-0 top-full my-3 bg-white border-0 max-h-0 transition-all duration-500 overflow-hidden z-10 border-neutral-200 rounded-lg',
                  overflowOpen && 'max-h-50vh border overflow-visible',
                )}
              >
                {hiddenLinks?.map((lk, index) => (
                  <div key={`${lk.id}_${index}`} className="p-3 relative group/hidden-link">
                    <DHeaderHiddenItem item={lk} />
                  </div>
                ))}
              </ul>
            </div>
          )
        }

        <DHeaderSearch ref={searchRef} />
      </div >
    </div >
  );
}
